/**
 * Sigma event handlers and rendering customization.
 * Mounts inside SigmaContainer to access sigma/graphology context.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useSigma, useRegisterEvents, useSetSettings, useCamera } from '@react-sigma/core';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import { buildGraphologyGraph } from './graphAdapter';
import { useGraphStore } from '@/stores/graph';
import type { GraphSnapshot, LayoutMode } from '@/services/api';

/**
 * Applies highlight/dim reduction for hover and selection state.
 */
function useNodeHighlighting() {
  const sigma = useSigma();
  const setSettings = useSetSettings();
  const { selectedNodeId, hoveredNodeId, highlightedNodeIds } = useGraphStore();
  const graph = sigma.getGraph();

  useEffect(() => {
    setSettings({
      nodeReducer: (node: string, data: Record<string, any>) => {
        const res = { ...data };
        const activeNode = hoveredNodeId ?? selectedNodeId;

        if (activeNode) {
          if (node === activeNode) {
            res.highlighted = true;
            res.size = (data.size ?? 5) * 1.3;
          } else if (graph.hasNode(activeNode) && graph.areNeighbors(node, activeNode)) {
            res.highlighted = true;
          } else {
            res.color = `${data.color ?? '#94a3b8'}40`;
            res.size = (data.size ?? 5) * 0.8;
          }
        }

        if (highlightedNodeIds.size > 0 && !activeNode) {
          if (highlightedNodeIds.has(node)) {
            res.highlighted = true;
            res.size = (data.size ?? 5) * 1.4;
          } else {
            res.color = `${data.color ?? '#94a3b8'}30`;
            res.size = (data.size ?? 5) * 0.7;
          }
        }

        return res;
      },
      edgeReducer: (edge: string, data: Record<string, any>) => {
        const res = { ...data };
        const activeNode = hoveredNodeId ?? selectedNodeId;

        if (activeNode && graph.hasNode(activeNode)) {
          const extremities = graph.extremities(edge);
          if (extremities.includes(activeNode)) {
            res.size = (data.size ?? 1) * 1.5;
          } else {
            res.color = `${data.color ?? '#334155'}15`;
            res.size = (data.size ?? 1) * 0.5;
          }
        }

        if (highlightedNodeIds.size > 0 && !activeNode) {
          const extremities = graph.extremities(edge);
          const anyHighlighted = extremities.some((n: string) => highlightedNodeIds.has(n));
          if (!anyHighlighted) {
            res.color = `${data.color ?? '#334155'}10`;
            res.size = (data.size ?? 1) * 0.3;
          }
        }

        return res;
      },
    });

    sigma.refresh();
  }, [selectedNodeId, hoveredNodeId, highlightedNodeIds, setSettings, sigma, graph]);
}

/**
 * Registers sigma event listeners for click, hover, etc.
 */
function useGraphEvents() {
  const sigma = useSigma();
  const registerEvents = useRegisterEvents();
  const { selectNode, hoverNode, navigateToNode } = useGraphStore();

  useEffect(() => {
    registerEvents({
      clickNode: (event: { node: string }) => {
        const currentSelected = useGraphStore.getState().selectedNodeId;
        if (currentSelected === event.node) {
          selectNode(null);
        } else {
          if (currentSelected) {
            navigateToNode(event.node);
          } else {
            selectNode(event.node);
          }
        }
      },
      clickStage: () => {
        selectNode(null);
        useGraphStore.getState().setHighlightedNodes(new Set());
      },
      enterNode: (event: { node: string }) => {
        hoverNode(event.node);
        const container = sigma.getContainer();
        container.style.cursor = 'pointer';
      },
      leaveNode: () => {
        hoverNode(null);
        const container = sigma.getContainer();
        container.style.cursor = 'default';
      },
    });
  }, [registerEvents, selectNode, hoverNode, navigateToNode, sigma]);
}

/**
 * Loads graph data and manages FA2 layout lifecycle.
 *
 * Key: we add nodes/edges directly to sigma's own graph instance
 * (rather than loading a separate graph object) so that FA2Layout's
 * web worker can mutate positions on the same graph sigma is rendering.
 */
export function GraphDataController({
  snapshot,
  layout,
}: {
  snapshot: GraphSnapshot | null;
  layout: LayoutMode;
}) {
  const sigma = useSigma();
  const { reset } = useCamera();
  const fa2Ref = useRef<FA2Layout | null>(null);

  const stopFA2 = useCallback(() => {
    if (fa2Ref.current) {
      fa2Ref.current.stop();
      fa2Ref.current.kill();
      fa2Ref.current = null;
    }
  }, []);

  useEffect(() => {
    if (!snapshot || snapshot.nodes.length === 0) return;

    stopFA2();

    // Build a temporary graph from the snapshot
    const tempGraph = buildGraphologyGraph(snapshot);

    // Copy into sigma's own graph so FA2 worker can mutate it in-place
    const sigmaGraph = sigma.getGraph();
    sigmaGraph.clear();

    tempGraph.forEachNode((node, attrs) => {
      sigmaGraph.addNode(node, { ...attrs });
    });
    tempGraph.forEachEdge((edge, attrs, source, target) => {
      try {
        sigmaGraph.addEdgeWithKey(edge, source, target, { ...attrs });
      } catch {
        // skip duplicate edges
      }
    });

    if (layout === 'force') {
      // FA2 operates on sigma's graph directly — positions update in real time
      const fa2 = new FA2Layout(sigmaGraph, {
        settings: {
          gravity: 0.1,
          scalingRatio: 4,
          barnesHutOptimize: sigmaGraph.order > 200,
          barnesHutTheta: 0.5,
          slowDown: 3,
          strongGravityMode: false,
          outboundAttractionDistribution: true,
        },
      });
      fa2Ref.current = fa2;
      fa2.start();

      // Auto-fit camera after initial layout stabilizes
      const fitTimer = setTimeout(() => {
        reset({ duration: 300 });
      }, 300);

      const stopTimer = setTimeout(() => {
        if (fa2Ref.current?.isRunning()) {
          fa2Ref.current.stop();
        }
      }, 5000);

      return () => {
        clearTimeout(fitTimer);
        clearTimeout(stopTimer);
        stopFA2();
      };
    }

    // Embedding layout — positions are already set, just fit the camera
    sigma.refresh();
    setTimeout(() => reset({ duration: 300 }), 100);

    return stopFA2;
  }, [snapshot, layout, sigma, stopFA2, reset]);

  useNodeHighlighting();
  useGraphEvents();

  return null;
}
